import React from 'react';
import { FileUp } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { pillPrimary, pillSecondary } from '../../ui/pills';
import { useUndo } from '../../ui/UndoBar';
import { useConfirm } from '../../ui/ConfirmProvider';
import { planningApi } from '../../../lib/api';
import type { Planning } from '../types';
import type { BilanImport, CodeAbsence, ModeEcriture, OngletDisponible, Rapprochement, RenfortPropose, SemaineLue } from '../../../lib/planningExcelImport/types';
import type { SalarieIAmani } from '../../../lib/planningExcelImport/rapprocherSalaries';
import { Etape1Semaine } from './Etape1Semaine';
import { Etape2Salaries } from './Etape2Salaries';
import { Etape3Ecriture } from './Etape3Ecriture';
import { RecapImport } from './RecapImport';

/**
 * L'assistant d'import d'un planning Excel (maquette 26b) — trois étapes, et le planning ne bouge qu'à
 * la dernière.
 *
 * Le classeur est lu dans le navigateur : le fichier du client, qui porte les horaires nominatifs de
 * tout son personnel, ne transite jamais par le serveur. `xlsx` et les modules de lecture sont chargés à
 * la demande, pour ne pas alourdir le planning de tous les jours.
 */
type Etape = 1 | 2 | 3 | 'fini';

export function ImportExcelModal({ isOpen, onClose, salaries, codesAbsence, semainesExistantes, typesRenfort, onImporte, onOuvrirReglages }: {
    isOpen: boolean;
    onClose: () => void;
    salaries: SalarieIAmani[];
    codesAbsence: CodeAbsence[];
    /** Les lundis déjà enregistrés, pour la mention « déjà importée » de l'étape 1. */
    semainesExistantes: string[];
    /** Les types de renfort que le planning propose à la main, pour préremplir les renforts relevés. */
    typesRenfort: string[];
    onImporte: (weekStart: string) => void;
    onOuvrirReglages: () => void;
}) {
    const { push } = useUndo();
    const confirmAction = useConfirm();
    const [etape, setEtape] = React.useState<Etape>(1);
    const [renforts, setRenforts] = React.useState<RenfortPropose[]>([]);
    const [fichier, setFichier] = React.useState<File | null>(null);
    const [classeur, setClasseur] = React.useState<any>(null);
    const [onglets, setOnglets] = React.useState<OngletDisponible[]>([]);
    const [ongletChoisi, setOngletChoisi] = React.useState<string | null>(null);
    const [semaine, setSemaine] = React.useState<SemaineLue | null>(null);
    const [rapprochements, setRapprochements] = React.useState<Rapprochement[]>([]);
    const [mode, setMode] = React.useState<ModeEcriture>('remplacer');
    const [bilan, setBilan] = React.useState<BilanImport | null>(null);
    const [planning, setPlanning] = React.useState<Planning | null>(null);
    const [codesACreer, setCodesACreer] = React.useState<CodeAbsence[]>([]);
    const [lecture, setLecture] = React.useState(false);
    const [travail, setTravail] = React.useState(false);
    const [erreur, setErreur] = React.useState<string | null>(null);

    const reinitialiser = () => {
        setEtape(1); setFichier(null); setClasseur(null); setOnglets([]); setOngletChoisi(null);
        setSemaine(null); setRapprochements([]); setMode('remplacer'); setBilan(null);
        setPlanning(null); setCodesACreer([]); setRenforts([]); setErreur(null);
    };

    React.useEffect(() => { if (isOpen) reinitialiser(); }, [isOpen]);

    const fermer = () => { onClose(); };

    const choisirFichier = async (choisi: File) => {
        setErreur(null); setLecture(true); setFichier(choisi);
        setOnglets([]); setOngletChoisi(null); setSemaine(null);
        try {
            const [{ ouvrirClasseur, listerOnglets }, { compterSalaries }] = await Promise.all([
                import('../../../lib/planningExcelImport/lireClasseur'),
                import('../../../lib/planningExcelImport/lireSemaine'),
            ]);
            const wb = await ouvrirClasseur(choisi);
            const liste = listerOnglets(wb, compterSalaries, new Set(semainesExistantes));
            setClasseur(wb);
            setOnglets(liste);
            // La semaine la plus récente qui n'a pas encore été importée : le choix le plus probable.
            const suggeree = [...liste].reverse().find((o) => !o.dejaImportee) || liste[liste.length - 1];
            if (suggeree) setOngletChoisi(suggeree.nom);
        } catch (e: any) {
            console.error('Lecture du classeur impossible', e);
            setErreur("Ce fichier n'a pas pu être ouvert. Vérifiez qu'il s'agit bien d'un classeur Excel.");
        } finally {
            setLecture(false);
        }
    };

    const lireLaSemaine = async () => {
        if (!classeur || !ongletChoisi) return;
        setTravail(true); setErreur(null);
        try {
            const [{ lireSemaine }, { indexerCodes }, { rapprocherSalaries }] = await Promise.all([
                import('../../../lib/planningExcelImport/lireSemaine'),
                import('../../../lib/planningExcelImport/lireCellule'),
                import('../../../lib/planningExcelImport/rapprocherSalaries'),
            ]);
            const lue = lireSemaine(classeur, ongletChoisi, indexerCodes(codesAbsence));
            const alias = await planningApi.listerAliasImport().catch(() => []);
            setSemaine(lue);
            setRapprochements(rapprocherSalaries(
                lue.lignes.map((l) => ({ nomFichier: l.nomFichier, ligneExcel: l.ligneExcel })),
                salaries,
                Array.isArray(alias) ? alias : [],
            ));
            setEtape(2);
        } catch (e: any) {
            console.error('Lecture de la semaine impossible', e);
            setErreur("Cette semaine n'a pas pu être lue.");
        } finally {
            setTravail(false);
        }
    };

    const preparerLEcriture = async () => {
        if (!semaine) return;
        setTravail(true); setErreur(null);
        try {
            const { construireSemaine, proposerRenforts } = await import('../../../lib/planningExcelImport/construireSemaine');
            const existant = await planningApi.getWeek(semaine.dates[0]).catch(() => null);
            const resultat = construireSemaine({
                semaine, rapprochements, planningExistant: existant || null, mode,
                codesConnus: codesAbsence, salaries, lotId: 'a-venir',
            });
            setPlanning(resultat.planning);
            setBilan(resultat.bilan);
            setCodesACreer(resultat.codesACreer);
            // Préremplis, jamais cochés : c'est à l'étape 3 que l'utilisateur confirme, ligne par ligne.
            setRenforts(proposerRenforts(resultat.bilan, typesRenfort));
            setEtape(3);
        } catch (e: any) {
            console.error('Préparation de l’import impossible', e);
            setErreur("L'import n'a pas pu être préparé.");
        } finally {
            setTravail(false);
        }
    };

    // Le mode d'écriture change ce qui sera écrit : on reconstruit plutôt que d'annoncer un bilan périmé.
    const changerMode = async (suivant: ModeEcriture) => {
        setMode(suivant);
        if (!semaine) return;
        try {
            const { construireSemaine } = await import('../../../lib/planningExcelImport/construireSemaine');
            const existant = await planningApi.getWeek(semaine.dates[0]).catch(() => null);
            const resultat = construireSemaine({
                semaine, rapprochements, planningExistant: existant || null, mode: suivant,
                codesConnus: codesAbsence, salaries, lotId: 'a-venir',
            });
            setPlanning(resultat.planning);
            setBilan(resultat.bilan);
            setCodesACreer(resultat.codesACreer);
        } catch (e) { console.error(e); }
    };

    const renfortsConfirmes = renforts.filter((r) => r.confirme);

    const modifierRenfort = (index: number, patch: Partial<RenfortPropose>) => setRenforts((prev) => prev.map((r) => {
        if (r.index !== index) return r;
        const suivant = { ...r, ...patch };
        // Un renfort qui redevient incomplet (horaire effacé) perd sa coche : rien n'est créé à moitié.
        if (!(suivant.label.trim() && suivant.debut && suivant.fin)) suivant.confirme = false;
        return suivant;
    }));

    const ecrire = async () => {
        if (!planning || !semaine || !bilan) return;
        const weekStart = semaine.dates[0];

        // Les renforts sont une création en plus des créneaux : on le dit avant, en clair.
        if (renfortsConfirmes.length > 0) {
            const n = renfortsConfirmes.length;
            const accepte = await confirmAction({
                title: `Créer ${n} renfort${n > 1 ? 's' : ''} dans le planning ?`,
                description: (
                    <>
                        En plus des {bilan.creneaux} créneaux, {n > 1 ? `${n} renforts seront ajoutés` : 'un renfort sera ajouté'} à la
                        semaine du {weekStart} :
                        <ul className="mt-1.5 pl-4 list-disc">
                            {renfortsConfirmes.map((r) => (
                                <li key={r.index}>{r.date} — {r.effectif} × {r.label}, {r.debut} → {r.fin}</li>
                            ))}
                        </ul>
                        L'annulation de l'import les retirera avec le reste.
                    </>
                ),
                confirmLabel: `Importer avec ${n} renfort${n > 1 ? 's' : ''}`,
            });
            if (!accepte) return;
        }

        setTravail(true); setErreur(null);
        try {
            const { ajouterRenforts } = await import('../../../lib/planningExcelImport/construireSemaine');
            const avecRenforts = ajouterRenforts(planning, renforts);
            const bilanFinal: BilanImport = { ...bilan, renfortsCrees: avecRenforts.crees };

            const reponse: any = await planningApi.creerLotImport({
                weekStart, planning: avecRenforts.planning, bilan: bilanFinal, codesCrees: codesACreer,
                fichier: fichier?.name || '', onglet: semaine.onglet, mode,
            });
            if (reponse?.error) throw new Error(reponse.error);
            setBilan(bilanFinal);

            // Les rapprochements retenus sont mémorisés : la semaine suivante ne reposera plus la question.
            const alias = rapprochements
                .filter((r) => r.retenu && r.employeeId)
                .map((r) => ({ nom_fichier: r.nomFichier, employee_id: String(r.employeeId) }));
            if (alias.length) await planningApi.enregistrerAliasImport(alias).catch(() => undefined);

            onImporte(weekStart);
            setEtape('fini');

            const lotId = reponse?.lotId;
            if (lotId) {
                const nbRenforts = avecRenforts.crees.length;
                push({
                    message: `Semaine du ${weekStart} importée — ${bilan.creneaux} créneaux${nbRenforts ? ` et ${nbRenforts} renfort${nbRenforts > 1 ? 's' : ''}` : ''}`,
                    undoneMessage: 'Import annulé, la semaine est revenue à son état précédent',
                    seconds: 15,
                    undo: async () => {
                        const retour: any = await planningApi.annulerLotImport(lotId);
                        if (retour?.error) throw new Error(retour.error);
                        onImporte(weekStart);
                    },
                });
            }
        } catch (e: any) {
            console.error('Import impossible', e);
            setErreur(e?.message || "L'import n'a pas pu être enregistré.");
        } finally {
            setTravail(false);
        }
    };

    const basculer = (nomFichier: string) => setRapprochements((prev) => prev.map((r) => (
        r.nomFichier === nomFichier ? { ...r, retenu: !r.retenu } : r
    )));

    const associer = (nomFichier: string, employeeId: string) => setRapprochements((prev) => prev.map((r) => {
        if (r.nomFichier !== nomFichier) return r;
        const salarie = salaries.find((s) => String(s.id) === String(employeeId));
        return {
            ...r, niveau: 'aConfirmer', employeeId: String(employeeId),
            employeeNom: salarie ? `${(salarie.last_name || '').toUpperCase()} ${salarie.first_name || ''}`.trim() : '',
            retenu: true,
        };
    }));

    const titres: Record<string, { titre: string; sousTitre: string }> = {
        1: { titre: 'Importer un planning Excel', sousTitre: 'Étape 1 sur 3 — quelle semaine' },
        2: { titre: 'Reconnaître les salariés', sousTitre: `Étape 2 sur 3 — ${semaine?.lignes.length || 0} noms lus dans ${semaine?.onglet || ''}` },
        3: { titre: 'Ce qui sera écrit', sousTitre: `Étape 3 sur 3 — ${semaine ? `semaine du ${semaine.dates[0]} au ${semaine.dates[6]}` : ''}` },
        fini: { titre: 'Import terminé', sousTitre: semaine ? `Semaine du ${semaine.dates[0]}` : '' },
    };
    const entete = titres[String(etape)];

    const pied = (
        <div className="flex items-center justify-between gap-2 w-full">
            {etape === 1 || etape === 'fini' ? (
                <button type="button" onClick={fermer} className={pillSecondary(36)}>
                    {etape === 'fini' ? 'Fermer' : 'Annuler'}
                </button>
            ) : (
                <button type="button" onClick={() => setEtape(etape === 3 ? 2 : 1)} className={pillSecondary(36)}>Retour</button>
            )}

            {etape === 1 && (
                <button type="button" onClick={lireLaSemaine} disabled={!ongletChoisi || travail} className={pillPrimary(36)}>
                    {travail ? 'Lecture…' : `Lire la semaine ${ongletChoisi ? ongletChoisi.replace(/^S0?/, '') : ''}`}
                </button>
            )}
            {etape === 2 && (
                <button type="button" onClick={preparerLEcriture} disabled={travail} className={pillPrimary(36)}>
                    {travail ? 'Préparation…' : 'Voir ce qui sera écrit'}
                </button>
            )}
            {etape === 3 && (
                <button type="button" onClick={ecrire} disabled={travail || !bilan?.creneaux && !bilan?.absences} className={pillPrimary(36)}>
                    {travail
                        ? 'Écriture…'
                        : `Importer ${bilan?.creneaux || 0} créneaux${renfortsConfirmes.length ? ` et ${renfortsConfirmes.length} renfort${renfortsConfirmes.length > 1 ? 's' : ''}` : ''}`}
                </button>
            )}
        </div>
    );

    return (
        <Modal
            open={isOpen}
            onClose={fermer}
            widthPx={500}
            icon={<FileUp size={16} />}
            title={entete?.titre}
            subtitle={entete?.sousTitre}
            footer={pied}
        >
            {erreur && (
                <div className="mb-3 px-[13px] py-2.5 rounded-[10px] bg-state-danger-soft border border-state-danger/30 text-[12px] leading-relaxed text-state-danger text-pretty">
                    {erreur}
                </div>
            )}

            {etape === 1 && (
                <Etape1Semaine
                    nomFichier={fichier?.name || null}
                    onglets={onglets}
                    choisi={ongletChoisi}
                    onChoisir={setOngletChoisi}
                    onFichier={choisirFichier}
                    lecture={lecture}
                />
            )}
            {etape === 2 && (
                <Etape2Salaries rapprochements={rapprochements} salaries={salaries} onBasculer={basculer} onAssocier={associer} />
            )}
            {etape === 3 && bilan && (
                <Etape3Ecriture
                    bilan={bilan}
                    mode={mode}
                    onMode={changerMode}
                    renforts={renforts}
                    typesRenfort={typesRenfort}
                    onRenfort={modifierRenfort}
                />
            )}
            {etape === 'fini' && bilan && (
                <RecapImport bilan={bilan} onOuvrirReglages={() => { fermer(); onOuvrirReglages(); }} />
            )}
        </Modal>
    );
}
